import React, { useContext } from 'react';

import { ContentState, convertToRaw } from 'draft-js';
import { useSelector } from 'react-redux';

import {
  startGeneration,
  retryGeneration,
  checkGenerationStatus,
  getGenerationResult,
  rateGeneration,
} from 'api/landingTextBlockGenerations';
import { EDetailsMode } from 'components/Pages/Admin/Itinerary/Details/types';
import LandingPageContext from 'components/shared/LandingPage/contexts/LandingPageContext';
import { EBlockType } from 'detailsv2/__blocks';
import { usePageContext } from 'detailsv2/LandingV2/Page/context';
import { useAIGeneration } from 'hooks/useAIGeneration';
import { showErrorSnackbar } from 'hooks/useSnackbar';
import { itinerariesItemLocalVersionSelector } from 'store/itinerariesItem/selectors';
import { pageLocalVersionSelector } from 'store/page/selectors';

import * as t from '../AIWysiwygWrapper.types';

export const useLandingTextBlockGeneration = (props: t.Props) => {
  const getStartGenerationParams = useStartGenerationParams(props.detailsMode);
  const [isShowPromptEditor, setIsShowPromptEditor] = React.useState(() => {
    if (props.data && typeof props.data === 'object' && props.data.initialEditor === 'ai-prompt') {
      return true;
    }

    return false;
  });

  const aiGeneration = useAIGeneration({
    startGenerationRequest: startGeneration,
    retryGenerationRequest: (generationData) => {
      return retryGeneration({
        generationData: generationData,
      });
    },
    checkStatusRequest: (generationData) => {
      return checkGenerationStatus({
        generationData: generationData,
      });
    },
    getResultRequest: (generationData) => {
      return getGenerationResult({
        generationData: generationData,
      });
    },
    rateGenerationRequest: (params: {
      generationData: { id: number };
      aditionalData: { evaluation: 'positive' | 'negative' };
    }) => {
      return rateGeneration({
        generationData: params.generationData,
        aditionalData: params.aditionalData,
      });
    },
  });

  const proposalLocalVersion = useSelector(itinerariesItemLocalVersionSelector);
  const pageLocalVersion = useSelector(pageLocalVersionSelector);

  React.useEffect(() => {
    // cancel generation and reset state after saving page/proposal
    // return callback run every time before next useEffect call
    return () => {
      try {
        aiGeneration.cancelGeneration();
        aiGeneration.changeState('idle');
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to cancel generation';
        showErrorSnackbar(errorMessage);
        console.error(error);
      }
    };
  }, [proposalLocalVersion, pageLocalVersion]);

  const onGenerate = async (message: string) => {
    try {
      const startParams = getStartGenerationParams(message);
      const response = await aiGeneration.generateAndPoll({ startParams });

      if (!response) {
        throw new Error('Invalid response from generation');
      }

      const newState = generateNewBlockStateFromText(response.content);
      props.onUpdate?.(newState);
      setIsShowPromptEditor(false);
      props.onAdd?.({ type: EBlockType.Text, data: { initialEditor: 'ai-prompt', text: '' } });
    } catch (e) {
      handleGenerationError(e);
    }
  };

  const onRetry = async () => {
    try {
      const response = await aiGeneration.retryAndPoll();

      if (!response) {
        throw new Error('Invalid response from retry');
      }

      const newState = generateNewBlockStateFromText(response.content);
      props.onUpdate?.(newState);
    } catch (e) {
      handleGenerationError(e);
    }
  };

  const onRate = async (rate: 'positive' | 'negative') => {
    try {
      return await aiGeneration.rateGeneration.mutateAsync({ evaluation: rate });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to rate generation';
      showErrorSnackbar(errorMessage);
      console.error(error);
    }
  };

  const onCancel = () => {
    try {
      aiGeneration.cancelGeneration();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to cancel generation';
      showErrorSnackbar(errorMessage);
      console.error(error);
    }
  };

  const handleGenerationError = (error: unknown) => {
    const errorMessage = (() => {
      if (!(error instanceof Error)) {
        return 'Failed to generate page';
      } else if (error.message.includes('Operation aborted')) {
        return 'The generation process has been canceled';
      } else if (error.message.includes('invalid_context')) {
        return 'Sorry, we’re unable to generate text for that request. Please try rephrasing or submitting a different request.';
      } else {
        return 'Failed to generate page';
      }
    })();

    showErrorSnackbar(errorMessage);
    console.error(error);
  };

  const generateNewBlockStateFromText = (text: string) => {
    const contentState = ContentState.createFromText(text);
    const newState = {
      type: 'text',
      data: {
        text: contentState.getPlainText(),
        raw: convertToRaw(contentState),
      },
      meta: {
        sourceGeneration: 'block-generation',
      },
    };
    return newState;
  };

  return {
    generationData: aiGeneration.generationData,
    isShowPromptEditor,
    isSuccess: aiGeneration.state === 'completed' || aiGeneration.state === 'retrying',
    isLoading: aiGeneration.state === 'generating' || aiGeneration.state === 'retrying',
    showPromptEditor: () => setIsShowPromptEditor(true),
    hidePromptEditor: () => setIsShowPromptEditor(false),
    onGenerate,
    onRetry,
    onRate,
    onCancel,
  };
};

const useStartGenerationParams = (detailsMode: EDetailsMode) => {
  const { landingData } = useContext(LandingPageContext);
  const isOriginalMode = detailsMode === EDetailsMode.Original;
  const page = usePageContext();

  const getOriginStartGenerationParams = (message: string) => {
    return { message, sourceId: landingData.id, sourceType: 'page' as const };
  };

  const getChronologicalStartGenerationParams = (message: string) => {
    // @ts-expect-error // TOFIX: Find a better way to understand if page is a trip event (Day N)
    const tripEventBlock = page.data.blocks.find((b: any) => b.type === EBlockType.TripEvent);

    const params = {
      message,
      ...(tripEventBlock
        ? {
            sourceId: tripEventBlock.tripEventId,
            sourceType: 'trip_event' as const,
          }
        : {
            sourceId: page.data.id,
            sourceType: 'page' as const,
          }),
    };
    return params;
  };

  return isOriginalMode ? getOriginStartGenerationParams : getChronologicalStartGenerationParams;
};
